import { gql } from '@apollo/client';
import type { Alignment, BackgroundColor } from '@snapchat/snap-design-system-marketing';

import { fragments as analyticsFragments } from '../../../../components/Analytics/query';
import type { AnalyticsDataProps } from '../../../../components/Analytics/types';
import type { ContentDataProps } from '../../../../components/Content';
import { fragments as contentFragments } from '../../../../components/Content';
import type {
  ContentfulExtraParametersFormFeature,
  ContentfulFormRow,
} from '../../../../components/Form';
import { extraParametersFormFeatureFragment, formRowFragment } from '../../../../components/Form';
import { type ImageDataProps, fragments as imageFragments } from '../../../../components/Image';
import { type VideoDataProps, fragments as videoFragments } from '../../../../components/Video';
import type { ContentfulTypedSysProps } from '../../../../types/contentful';
import type { Items } from '../../../../types/Items';
import type { RichText } from '../../../../types/RichText';
import { contentfulSysIdFragment } from '../../../../utils/contentful/contentfulQuery';
import {
  cventConnectorFragment,
  salesforceConnectorFragment,
  splashConnectorFragment,
  zkipsterConnectorFragment,
} from '../fragments';
import type { CventConnectorDataProps, SalesforceConnectorDataProps } from '../types';

export const fragments = {
  all: gql`
    fragment RegistrationFormBlockAll on RegistrationFormBlock {
      ...ContentfulSysId
      eyebrow
      title {
        json
      }
      subtitle {
        json
      }
      titleAlignmentDesktop
      titleAlignmentMobile
      backgroundColor
      backgroundMediaV2 {
        __typename
        ... on Video {
          ...VideoAll
        }
        ... on Image {
          ...ImageAll
        }
      }
      fullHeight
      formRowsCollection {
        items {
          ...FormRowAll
        }
      }
      additionalFeaturesCollection(limit: 5) {
        items {
          ... on ExtraParametersFormFeature {
            ...ExtraParametersFormFeatureAll
          }
        }
      }
      formAnalytics {
        ...AnalyticsAll
      }
      connector {
        ... on SalesforceConnector {
          ...SalesforceConnectorAll
        }
        ... on CventConnector {
          ...CventConnectorAll
        }
        ... on ZkipsterConnector {
          ...ZkipsterConnectorAll
        }
        ... on SplashConnector {
          ...SplashConnectorAll
        }
      }
      confirmationSlug {
        ...ContentfulSysId
        slug
      }
      rememberRegistration
      unknownErrorText {
        json
      }
      sideContent {
        ...ContentAll
      }
    }
    ${formRowFragment}
    ${extraParametersFormFeatureFragment}
    ${contentFragments.all}
    ${analyticsFragments.all}
    ${videoFragments.all}
    ${imageFragments.all}
    ${salesforceConnectorFragment}
    ${cventConnectorFragment}
    ${zkipsterConnectorFragment}
    ${splashConnectorFragment}
    ${contentfulSysIdFragment}
  `,
};

export const query = gql`
  query RegistrationBlockQuery($preview: Boolean!, $locale: String!, $id: String!) {
    registrationFormBlock(preview: $preview, locale: $locale, id: $id) {
      ...RegistrationFormBlockAll
    }
  }
  ${fragments.all}
`;

export interface RegistrationBlockData extends ContentfulTypedSysProps<'RegistrationBlock'> {
  registrationFormBlock: {
    eyebrow?: string;
    title?: RichText;
    subtitle?: RichText;
    titleAlignmentDesktop?: Alignment;
    titleAlignmentMobile?: Alignment;
    backgroundColor?: BackgroundColor;
    backgroundMediaV2?: VideoDataProps | ImageDataProps;
    fullHeight?: boolean;
    formRowsCollection?: Items<ContentfulFormRow>;
    additionalFeaturesCollection?: Items<ContentfulExtraParametersFormFeature>;
    formAnalytics?: AnalyticsDataProps;
    connector?: CventConnectorDataProps | SalesforceConnectorDataProps;
    confirmationSlug?: { slug: string };
    rememberRegistration?: boolean;
    unknownErrorText?: RichText;
    sideContent?: ContentDataProps;
  };
}
